import { useEffect } from 'react';
import {
    Box, Stack, Badge, Button, Group, Modal, TextInput, Select, MultiSelect, Text, Loader, Center, ActionIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Plus, Trash2 } from 'lucide-react';
import { Roles } from '@bmai/shared';
import type { Permission } from '@bmai/shared';
import { useAuth } from '../auth/AuthContext';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { UsersAdminPageViewModel } from './UsersAdminPageViewModel';

const CAN_MANAGE_USERS: Permission = { action: 'manage', resource: 'users' };

export function UsersPage(): React.ReactElement {
    const { user: currentUser, hasPermission } = useAuth();
    const canManageUsers = hasPermission(CAN_MANAGE_USERS);
    const canInvite = canManageUsers;
    const canManageRoles = canManageUsers;
    const canDelete = canManageUsers;

    const vm = useViewModel(UsersAdminPageViewModel);

    useEffect(() => {
        void vm.load();
    }, [vm]);

    async function handleInvite(): Promise<void> {
        const result = await vm.invite(currentUser?.tenantId);
        if (result.success) {
            notifications.show({ title: 'User invited', message: `Invite sent to ${result.email}`, color: 'green' });
        } else if (result.error) {
            notifications.show({ title: 'Error', message: result.error, color: 'red' });
        }
    }

    async function handleRolesChange(userId: string, newRoles: string[]): Promise<void> {
        const result = await vm.updateRoles(userId, newRoles);
        if (result.success) {
            notifications.show({ title: 'Roles updated', message: result.roles.join(', '), color: 'green' });
        } else if (result.error) {
            notifications.show({ title: 'Error', message: result.error, color: 'red' });
        }
    }

    async function handleDelete(userId: string, email: string): Promise<void> {
        if (!confirm(`Delete user ${email}?`)) return;
        const result = await vm.deleteUser(userId, email);
        if (result.success) {
            notifications.show({ title: 'User deleted', message: result.email, color: 'green' });
        } else if (result.error) {
            notifications.show({ title: 'Error', message: result.error, color: 'red' });
        }
    }

    if (vm.loading) return <Center><Loader /></Center>;

    type UserRow = NonNullable<typeof vm.users>[number];

    const columns: MegaGridColumn<UserRow>[] = [
        { key: 'name', header: 'Name', sortValue: (u) => u.displayName, render: (u) => <Text size="sm" fw={500}>{u.displayName}</Text> },
        { key: 'email', header: 'Email', sortValue: (u) => u.email, render: (u) => <Text size="sm">{u.email}</Text> },
        {
            key: 'roles',
            header: 'Roles',
            sortValue: (u) => [...u.roles].join(','),
            render: (u) => (
                <Group gap={4}>
                    {u.roles.map((r) => (
                        <Badge key={r} size="sm" variant="light" color={r === Roles.ADMIN ? 'red' : 'blue'}>{r}</Badge>
                    ))}
                    {u.roles.length === 0 && <Text size="sm" c="dimmed">No roles</Text>}
                </Group>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            sortValue: (u) => u.status,
            filter: { type: 'enum', accessor: (u) => u.status },
            render: (u) => <Badge size="sm" color={u.status === 'active' ? 'green' : 'gray'}>{u.status}</Badge>,
        },
    ];

    if (canManageRoles || canDelete) {
        columns.push({
            key: 'actions',
            header: 'Actions',
            hideable: false,
            resizable: false,
            width: 280,
            render: (u) => (
                <Group gap={4}>
                    {canManageRoles && (
                        <MultiSelect
                            size="xs"
                            placeholder="Set roles"
                            data={vm.roleOptions}
                            value={[...u.roles]}
                            w={220}
                            onChange={(val) => handleRolesChange(u.id, val)}
                            clearable
                        />
                    )}
                    {canDelete && (
                        <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleDelete(u.id, u.email)}>
                            <Trash2 size={14} />
                        </ActionIcon>
                    )}
                </Group>
            ),
        });
    }

    return (
        <Box>
            <PageHeaderComponent
                title="Users"
                subtitle="Invite users and manage their roles."
                rightToolbar={canInvite ? <Button leftSection={<Plus size={16} />} onClick={() => vm.openInvite()}>Invite User</Button> : undefined}
            />

            <MegaGrid
                persistenceKey="admin-users"
                columns={columns}
                rows={vm.users ?? []}
                getRowKey={(u) => u.id}
                searchAccessor={(u) => `${u.displayName} ${u.email} ${[...u.roles].join(' ')}`}
                searchPlaceholder="Search users"
                clientPageSize={25}
                striped
                emptyMessage="No users in this tenant"
            />

            <Modal opened={vm.inviteOpen} onClose={() => vm.closeInvite()} title="Invite User" size="md">
                <Stack>
                    <TextInput
                        label="Email"
                        placeholder="user@company.com"
                        required
                        value={vm.inviteForm.email}
                        onChange={(e) => vm.setInviteField('email', e.currentTarget.value)}
                    />
                    <Group grow>
                        <TextInput
                            label="First Name"
                            value={vm.inviteForm.firstName}
                            onChange={(e) => vm.setInviteField('firstName', e.currentTarget.value)}
                        />
                        <TextInput
                            label="Last Name"
                            value={vm.inviteForm.lastName}
                            onChange={(e) => vm.setInviteField('lastName', e.currentTarget.value)}
                        />
                    </Group>
                    <Select
                        label="Role"
                        data={vm.roleOptions}
                        value={vm.inviteForm.role}
                        onChange={(val) => val && vm.setInviteField('role', val)}
                    />
                    <Button onClick={handleInvite} loading={vm.saving} fullWidth mt="sm">Send Invite</Button>
                </Stack>
            </Modal>
        </Box>
    );
}
